import React, { useState } from 'react';
import Cookies from 'js-cookie';
import {
  X,
  MessageSquare,
  Table2,
  BarChart3,
  FileText,
  Sparkles,
  Info,
  Calendar
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';

const BlockConfigurator = ({ block, dataSources, planId, onSave, onClose }) => {
  const [config, setConfig] = useState({
    title: block.title || '',
    config: { ...block.config }
  });

  const handleSave = () => {
    onSave(config);
  };

  // Render different configurators based on block type
  const renderConfigurator = () => {
    switch (block.type) {
      case 'narrative':
        return <NarrativeConfigurator config={config} setConfig={setConfig} />;
      case 'data_section':
        return <DataSectionConfigurator config={config} setConfig={setConfig} dataSources={dataSources} />;
      case 'chart':
        return <ChartConfigurator config={config} setConfig={setConfig} dataSources={dataSources} planId={planId} />;
      case 'custom_table':
        return <CustomTableConfigurator config={config} setConfig={setConfig} />;
      case 'cover_page':
        return <CoverPageConfigurator config={config} setConfig={setConfig} />;
      case 'table_of_contents':
        return <TOCConfigurator config={config} setConfig={setConfig} />;
      
      // Business Plan Input Sections
      case 'section_strategic_framework':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="strategic_framework"
          sectionTitle="Strategic Framework"
          tabs={[
            { id: 'objectives', label: 'Strategic Objectives', description: 'Business goals and objectives' },
            { id: 'initiatives', label: 'Strategic Initiatives', description: 'Projects to achieve objectives' },
            { id: 'revenue_streams', label: 'Revenue Streams', description: 'Revenue categories' },
            { id: 'products', label: 'Products & Services', description: 'Product catalog with pricing' },
            { id: 'sales_forecasts', label: 'Sales Forecasts', description: 'Projected sales by product' }
          ]}
        />;
      case 'section_cost_centers':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="cost_centers"
          sectionTitle="Cost Centers"
          tabs={[
            { id: 'centers', label: 'Cost Centers', description: 'Organizational cost centers' },
            { id: 'allocations', label: 'Cost Allocations', description: 'How costs are allocated' }
          ]}
        />;
      case 'section_cost_management':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="cost_management"
          sectionTitle="Cost Management"
          tabs={[
            { id: 'core_costs', label: 'Core Costs', description: 'Essential operating costs' },
            { id: 'operations', label: 'Operations', description: 'Operational expenses' },
            { id: 'investments', label: 'Investments', description: 'Capital investments' },
            { id: 'financial', label: 'Financial Costs', description: 'Interest and fees' }
          ]}
        />;
      case 'section_financial_management':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="financial_management"
          sectionTitle="Financial Management"
          tabs={[
            { id: 'current_debt', label: 'Current Debt', description: 'Existing loans and liabilities' },
            { id: 'current_equity', label: 'Current Equity', description: 'Existing equity structure' },
            { id: 'capital_structure', label: 'Capital Structure', description: 'Overall capital composition' }
          ]}
        />;
      case 'section_financial_planning':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="financial_planning"
          sectionTitle="Financial Planning"
          tabs={[
            { id: 'overview', label: 'Overview', description: 'Financial planning summary' },
            { id: 'planned_debt', label: 'Planned Debt', description: 'Future debt financing' },
            { id: 'planned_equity', label: 'Planned Equity', description: 'Future equity raises' },
            { id: 'milestones', label: 'Milestones', description: 'Financial milestones' }
          ]}
        />;
      
      // Financial Output Sections
      case 'section_financial_statements':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="financial_statements"
          sectionTitle="Financial Statements"
          tabs={[
            { id: 'income_statement', label: 'Income Statement', description: 'P&L, Ratios, Revenue Trends' },
            { id: 'balance_sheet', label: 'Balance Sheet', description: 'Assets, Liabilities, Equity' },
            { id: 'cash_flow', label: 'Cash Flow', description: 'Cash flow statement' },
            { id: 'cost_analysis', label: 'Cost Analysis', description: 'Cost breakdown and trends' }
          ]}
        />;
      case 'section_financial_analytics':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="financial_analytics"
          sectionTitle="Financial Analytics"
          tabs={[
            { id: 'financial', label: 'Financial Statements', description: 'Calculated statements' },
            { id: 'breakeven', label: 'Break-Even Analysis', description: 'Break-even point' },
            { id: 'roi', label: 'ROI Analysis', description: 'Return on investment' },
            { id: 'cashflow', label: 'Cash Flow Analysis', description: 'Cash flow projections' }
          ]}
        />;
      case 'section_planning_dashboard':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="planning_dashboard"
          sectionTitle="Planning Dashboard"
          tabs={[
            { id: 'kpis', label: 'KPI Summary', description: 'Key performance indicators' },
            { id: 'charts', label: 'Charts & Visualizations', description: 'Dashboard charts' },
            { id: 'summaries', label: 'Financial Summaries', description: 'Summary tables' }
          ]}
        />;
      case 'section_scenario_modeling':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="scenario_modeling"
          sectionTitle="Scenario Modeling"
          tabs={[
            { id: 'scenarios', label: 'Scenarios', description: 'Different business scenarios' },
            { id: 'comparisons', label: 'Scenario Comparisons', description: 'Compare scenario outcomes' }
          ]}
        />;
      
      // Performance Sections
      case 'section_executive_dashboard':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="executive_dashboard"
          sectionTitle="Executive Dashboard"
          tabs={[
            { id: 'overview', label: 'Performance Overview', description: 'KPIs and summary' },
            { id: 'highlights', label: 'Key Highlights', description: 'Top metrics and alerts' }
          ]}
        />;
      case 'section_revenue_analysis':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="revenue_analysis"
          sectionTitle="Revenue Analysis"
          tabs={[
            { id: 'actuals', label: 'Actual Revenue', description: 'Real sales data' },
            { id: 'by_product', label: 'Revenue by Product', description: 'Product breakdown' },
            { id: 'comparisons', label: 'Plan vs Actual', description: 'Comparison analysis' }
          ]}
        />;
      case 'section_cost_analysis':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="cost_analysis_performance"
          sectionTitle="Cost Analysis"
          tabs={[
            { id: 'actuals', label: 'Actual Costs', description: 'Real expense data' },
            { id: 'by_category', label: 'Costs by Category', description: 'Category breakdown' },
            { id: 'trends', label: 'Cost Trends', description: 'Historical trends' }
          ]}
        />;
      case 'section_profitability':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="profitability"
          sectionTitle="Profitability Analysis"
          tabs={[
            { id: 'margins', label: 'Profit Margins', description: 'Gross and net margins' },
            { id: 'pl_comparison', label: 'P&L Comparison', description: 'Plan vs actual P&L' }
          ]}
        />;
      case 'section_variance':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="variance"
          sectionTitle="Variance Analytics"
          tabs={[
            { id: 'revenue', label: 'Revenue Variance', description: 'Actual vs planned revenue' },
            { id: 'cost', label: 'Cost Variance', description: 'Actual vs planned costs' },
            { id: 'profit', label: 'Profit Variance', description: 'Actual vs planned profit' }
          ]}
        />;
      case 'section_trends':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="trends"
          sectionTitle="Trends & Forecasting"
          tabs={[
            { id: 'revenue_trends', label: 'Revenue Trends', description: 'Historical revenue patterns' },
            { id: 'cost_trends', label: 'Cost Trends', description: 'Historical cost patterns' },
            { id: 'forecasts', label: 'Forecasts', description: 'Future projections' }
          ]}
        />;
      
      // Company Section
      case 'section_company_profile':
        return <SectionConfigurator 
          config={config} 
          setConfig={setConfig} 
          sectionType="company"
          sectionTitle="Company Profile"
          tabs={[
            { id: 'details', label: 'Company Details', description: 'Basic company info' },
            { id: 'team', label: 'Team & Management', description: 'Key team members' }
          ]}
        />;
        
      default:
        return <div className="text-gray-500">No configuration available for this block type.</div>;
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Configure: {block.title || block.type}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Block Title */}
          <div className="space-y-2">
            <Label>Block Title</Label>
            <Input
              value={config.title}
              onChange={(e) => setConfig({ ...config, title: e.target.value })}
              placeholder="Enter block title"
            />
          </div>

          {/* Type-specific configuration */}
          {renderConfigurator()}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Apply Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==========================================
// Time Period Configuration Component
// ==========================================
const TimePeriodConfig = ({ config, setConfig, dataSources }) => {
  const periodTypes = dataSources?.period_types || [
    { id: 'daily', name: 'Daily' },
    { id: 'weekly', name: 'Weekly' },
    { id: 'monthly', name: 'Monthly' },
    { id: 'quarterly', name: 'Quarterly' },
    { id: 'annual', name: 'Annual' }
  ];

  return (
    <div className="space-y-3 p-3 bg-gray-50 rounded-lg border">
      <Label className="text-sm font-medium flex items-center gap-2">
        <Calendar className="h-4 w-4" />
        Time Period
      </Label>
      
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs text-gray-500">Period Type</Label>
          <Select
            value={config.config?.period_config?.period_type || 'monthly'}
            onValueChange={(value) => setConfig({
              ...config,
              config: {
                ...config.config,
                period_config: { ...config.config?.period_config, period_type: value }
              }
            })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periodTypes.map((pt) => (
                <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label className="text-xs text-gray-500">Mode</Label>
          <Select
            value={config.config?.period_config?.mode || 'calendar'}
            onValueChange={(value) => setConfig({
              ...config,
              config: {
                ...config.config,
                period_config: { ...config.config?.period_config, mode: value }
              }
            })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="calendar">Calendar Year</SelectItem>
              <SelectItem value="fiscal">Fiscal Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs text-gray-500">Start Date</Label>
          <Input
            type="date"
            value={config.config?.period_config?.start_date || ''}
            onChange={(e) => setConfig({
              ...config,
              config: {
                ...config.config,
                period_config: { ...config.config?.period_config, start_date: e.target.value }
              }
            })}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs text-gray-500">End Date</Label>
          <Input
            type="date"
            value={config.config?.period_config?.end_date || ''}
            onChange={(e) => setConfig({
              ...config,
              config: {
                ...config.config,
                period_config: { ...config.config?.period_config, end_date: e.target.value }
              }
            })}
          />
        </div>
      </div>
    </div>
  );
};

// ==========================================
// Narrative Block Configurator
// ==========================================
const NarrativeConfigurator = ({ config, setConfig }) => {
  const [source, setSource] = useState(config.config?.source || 'ai_generated');

  const handleSourceChange = (newSource) => {
    setSource(newSource);
    setConfig({
      ...config,
      config: { ...config.config, source: newSource }
    });
  };

  const narrativeTypes = [
    { id: 'executive_summary', label: 'Executive Summary' },
    { id: 'financial_overview', label: 'Financial Overview' },
    { id: 'market_analysis', label: 'Market Analysis' },
    { id: 'investment_rationale', label: 'Investment Rationale' },
    { id: 'custom', label: 'Custom Narrative' }
  ];

  const focusAreas = [
    { id: 'financial_highlights', label: 'Financial Highlights' },
    { id: 'growth_potential', label: 'Growth Potential' },
    { id: 'loan_purpose', label: 'Loan Purpose & Utilization' },
    { id: 'market_position', label: 'Market Position' },
    { id: 'risk_factors', label: 'Risk Factors' },
    { id: 'management_team', label: 'Management Team' }
  ];

  return (
    <div className="space-y-4">
      {/* Source Selection */}
      <div className="space-y-2">
        <Label>Content Source</Label>
        <div className="space-y-2">
          <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="source"
              value="ai_generated"
              checked={source === 'ai_generated'}
              onChange={() => handleSourceChange('ai_generated')}
              className="mt-1"
            />
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                <span className="font-medium">AI Generated</span>
              </div>
              <p className="text-sm text-gray-500">Content will be automatically generated when you export the report</p>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="source"
              value="custom_text"
              checked={source === 'custom_text'}
              onChange={() => handleSourceChange('custom_text')}
              className="mt-1"
            />
            <div>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-blue-500" />
                <span className="font-medium">Custom Text</span>
              </div>
              <p className="text-sm text-gray-500">Write your own content</p>
            </div>
          </label>
        </div>
      </div>

      {/* AI Settings */}
      {source === 'ai_generated' && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-700">
              AI content will be generated automatically when you click "Generate Report" or "Export".
            </p>
          </div>

          <div className="space-y-2">
            <Label>Narrative Type</Label>
            <Select
              value={config.config?.ai_settings?.narrative_type || 'executive_summary'}
              onValueChange={(value) => setConfig({
                ...config,
                config: {
                  ...config.config,
                  ai_settings: { ...config.config?.ai_settings, narrative_type: value }
                }
              })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {narrativeTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tone</Label>
              <Select
                value={config.config?.ai_settings?.tone || 'formal'}
                onValueChange={(value) => setConfig({
                  ...config,
                  config: {
                    ...config.config,
                    ai_settings: { ...config.config?.ai_settings, tone: value }
                  }
                })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="formal">Professional & Formal</SelectItem>
                  <SelectItem value="casual">Conversational</SelectItem>
                  <SelectItem value="persuasive">Persuasive</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Length</Label>
              <Select
                value={config.config?.ai_settings?.length || 'standard'}
                onValueChange={(value) => setConfig({
                  ...config,
                  config: {
                    ...config.config,
                    ai_settings: { ...config.config?.ai_settings, length: value }
                  }
                })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="brief">Brief</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="detailed">Detailed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Language</Label>
            <Select
              value={config.config?.ai_settings?.language || 'en'}
              onValueChange={(value) => setConfig({
                ...config,
                config: {
                  ...config.config,
                  ai_settings: { ...config.config?.ai_settings, language: value }
                }
              })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="am">አማርኛ (Amharic)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Focus Areas</Label>
            <div className="grid grid-cols-2 gap-2">
              {focusAreas.map((area) => (
                <label key={area.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={config.config?.ai_settings?.focus_areas?.includes(area.id)}
                    onCheckedChange={(checked) => {
                      const current = config.config?.ai_settings?.focus_areas || [];
                      const updated = checked
                        ? [...current, area.id]
                        : current.filter(f => f !== area.id);
                      setConfig({
                        ...config,
                        config: {
                          ...config.config,
                          ai_settings: { ...config.config?.ai_settings, focus_areas: updated }
                        }
                      });
                    }}
                  />
                  {area.label}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Custom Text */}
      {source === 'custom_text' && (
        <div className="space-y-2">
          <Label>Content</Label>
          <Textarea
            value={config.config?.custom_text || ''}
            onChange={(e) => setConfig({
              ...config,
              config: { ...config.config, custom_text: e.target.value }
            })}
            placeholder="Enter your custom text here..."
            rows={8}
          />
        </div>
      )}
    </div>
  );
};

// ==========================================
// Data Section Configurator
// ==========================================
const DataSectionConfigurator = ({ config, setConfig, dataSources }) => {
  const dataSourceOptions = dataSources?.data_sources || {};
  const hierarchicalCatalog = dataSources?.hierarchical_catalog || {};
  
  // Build hierarchical structure from catalog
  const buildHierarchicalOptions = () => {
    const options = [];
    
    // Process each main section
    Object.entries(hierarchicalCatalog).forEach(([sectionKey, section]) => {
      const sectionLabel = section.label || sectionKey;
      
      // Process pages in section
      if (section.pages) {
        Object.entries(section.pages).forEach(([pageKey, page]) => {
          const pageLabel = page.label || pageKey;
          
          // Process tabs in page
          if (page.tabs) {
            Object.entries(page.tabs).forEach(([tabKey, tab]) => {
              const tabLabel = tab.label || tabKey;
              
              // Check for sub-tabs
              if (tab.sub_tabs) {
                Object.entries(tab.sub_tabs).forEach(([subTabKey, subTab]) => {
                  const subTabLabel = subTab.label || subTabKey;
                  const category = `${sectionLabel} > ${pageLabel} > ${tabLabel} > ${subTabLabel}`;
                  
                  if (subTab.components) {
                    subTab.components.forEach(comp => {
                      options.push({
                        key: comp.key,
                        name: comp.name,
                        type: comp.type,
                        category: category,
                        path: [sectionLabel, pageLabel, tabLabel, subTabLabel]
                      });
                    });
                  }
                });
              }
              
              // Components directly in tab (no sub-tabs)
              if (tab.components) {
                const category = `${sectionLabel} > ${pageLabel} > ${tabLabel}`;
                tab.components.forEach(comp => {
                  options.push({
                    key: comp.key,
                    name: comp.name,
                    type: comp.type,
                    category: category,
                    path: [sectionLabel, pageLabel, tabLabel]
                  });
                });
              }
            });
          }
        });
      }
    });
    
    return options;
  };
  
  const hierarchicalOptions = buildHierarchicalOptions();
  
  // Group options by category path (Section > Page > Tab)
  const groupedByPath = {};
  hierarchicalOptions.forEach(opt => {
    const pathKey = opt.category;
    if (!groupedByPath[pathKey]) groupedByPath[pathKey] = [];
    groupedByPath[pathKey].push(opt);
  });
  
  // Also include flat sources for backward compatibility and anything not in hierarchy
  const flatSources = Object.entries(dataSourceOptions).filter(([key]) => 
    !hierarchicalOptions.some(opt => opt.key === key)
  );
  
  if (flatSources.length > 0) {
    flatSources.forEach(([key, source]) => {
      const cat = source.category || 'Other';
      if (!groupedByPath[cat]) groupedByPath[cat] = [];
      groupedByPath[cat].push({ 
        key, 
        name: source.name, 
        type: 'table',
        category: cat,
        description: source.description
      });
    });
  }
  
  // Sort categories for consistent display
  const sortedCategories = Object.keys(groupedByPath).sort();

  return (
    <div className="space-y-4">
      {/* AI Description Toggle */}
      <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <Sparkles className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-amber-800">AI-Generated Description</span>
            <Checkbox
              checked={config.config?.include_ai_description !== false}
              onCheckedChange={(checked) => setConfig({
                ...config,
                config: { ...config.config, include_ai_description: checked }
              })}
            />
          </div>
          <p className="text-xs text-amber-700 mt-1">
            AI will automatically describe this data section when you generate the report
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Data Source</Label>
        <Select
          value={config.config?.source || ''}
          onValueChange={(value) => setConfig({
            ...config,
            config: { ...config.config, source: value }
          })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select data source" />
          </SelectTrigger>
          <SelectContent className="max-h-96 overflow-y-auto">
            {sortedCategories.map((category) => (
              <div key={category}>
                <div className="px-2 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 sticky top-0 z-10 border-b border-slate-200">
                  {category}
                </div>
                {groupedByPath[category].map((source) => (
                  <SelectItem 
                    key={source.key} 
                    value={source.key}
                    className="pl-4"
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        source.type === 'chart' ? 'bg-blue-100 text-blue-700' :
                        source.type === 'kpi' ? 'bg-purple-100 text-purple-700' :
                        source.type === 'info' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {source.type === 'chart' ? '📊' : 
                         source.type === 'kpi' ? '📈' : 
                         source.type === 'info' ? 'ℹ️' : '📋'}
                      </span>
                      <span>{source.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
        {config.config?.source && dataSourceOptions[config.config.source] && (
          <p className="text-xs text-gray-500">
            {dataSourceOptions[config.config.source].description}
          </p>
        )}
      </div>

      {/* Time Period Configuration */}
      <TimePeriodConfig config={config} setConfig={setConfig} dataSources={dataSources} />

      <div className="space-y-2">
        <Label>Detail Level</Label>
        <Select
          value={config.config?.detail_level || 'standard'}
          onValueChange={(value) => setConfig({
            ...config,
            config: { ...config.config, detail_level: value }
          })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="summary">Summary - Key metrics only</SelectItem>
            <SelectItem value="standard">Standard - Main tables</SelectItem>
            <SelectItem value="detailed">Detailed - All line items</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={config.config?.include_chart}
            onCheckedChange={(checked) => setConfig({
              ...config,
              config: { ...config.config, include_chart: checked }
            })}
          />
          <Label>Include chart visualization</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={config.config?.show_comparison}
            onCheckedChange={(checked) => setConfig({
              ...config,
              config: { ...config.config, show_comparison: checked }
            })}
          />
          <Label>Show Actual vs Planned comparison</Label>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// Chart Configurator with Real-Time Preview (R3.3)
// ==========================================
const ChartConfigurator = ({ config, setConfig, dataSources, planId }) => {
  const chartSources = dataSources?.chart_sources || {};
  const [previewData, setPreviewData] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState(null);
  
  // Group chart sources by category
  const groupedCharts = {};
  Object.entries(chartSources).forEach(([key, source]) => {
    const cat = source.category || 'Charts';
    if (!groupedCharts[cat]) groupedCharts[cat] = [];
    groupedCharts[cat].push({ key, ...source });
  });
  
  // Sort categories
  const sortedChartCategories = Object.keys(groupedCharts).sort();

  // Fetch preview data when source changes
  const fetchPreview = React.useCallback(async () => {
    const source = config.config?.source;
    if (!source || !planId) {
      setPreviewData(null);
      return;
    }
    
    setPreviewLoading(true);
    setPreviewError(null);
    
    try {
      // Get token from cookies (primary) or localStorage (fallback)
      const token = Cookies.get('enakid_token') || localStorage.getItem('enakid_token') || localStorage.getItem('token');
      
      if (!token) {
        setPreviewError('Not authenticated');
        setPreviewLoading(false);
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/report-templates/aggregate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          data_type: source.includes('cost') ? 'cost' : source.includes('profit') ? 'profit' : 'revenue',
          plan_id: planId,
          period_config: {
            period_type: config.config?.period_type || 'monthly',
            start_date: config.config?.start_date,
            end_date: config.config?.end_date
          }
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setPreviewData(result.data);
      } else {
        const errorText = await response.text();
        console.error('Preview fetch failed:', response.status, errorText);
        setPreviewError('Failed to load preview data');
      }
    } catch (err) {
      console.error('Preview fetch error:', err);
      setPreviewError('Error loading preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [config.config?.source, config.config?.period_type, config.config?.start_date, config.config?.end_date, planId]);

  // Debounced fetch on config change
  React.useEffect(() => {
    const timeoutId = setTimeout(fetchPreview, 500);
    return () => clearTimeout(timeoutId);
  }, [fetchPreview]);

  return (
    <div className="space-y-4">
      {/* AI Insights Toggle */}
      <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <Sparkles className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-amber-800">AI-Generated Insights</span>
            <Checkbox
              checked={config.config?.include_ai_insights !== false}
              onCheckedChange={(checked) => setConfig({
                ...config,
                config: { ...config.config, include_ai_insights: checked }
              })}
            />
          </div>
          <p className="text-xs text-amber-700 mt-1">
            AI will automatically analyze and describe this chart when you generate the report
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Chart Data Source</Label>
        <Select
          value={config.config?.source || ''}
          onValueChange={(value) => setConfig({
            ...config,
            config: { ...config.config, source: value }
          })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select chart data" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            {sortedChartCategories.map((category) => (
              <div key={category}>
                <div className="px-2 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 sticky top-0 z-10 border-b border-slate-200">
                  {category}
                </div>
                {groupedCharts[category].map((source) => (
                  <SelectItem key={source.key} value={source.key} className="pl-4">
                    <div className="flex flex-col">
                      <span>{source.name}</span>
                      {source.chart_types && (
                        <span className="text-xs text-gray-500">
                          Types: {source.chart_types.join(', ')}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
        {config.config?.source && chartSources[config.config.source] && (
          <p className="text-xs text-gray-500">
            {chartSources[config.config.source].description}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Chart Type</Label>
        <Select
          value={config.config?.chart_type || 'bar'}
          onValueChange={(value) => setConfig({
            ...config,
            config: { ...config.config, chart_type: value }
          })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="bar">Bar Chart</SelectItem>
            <SelectItem value="line">Line Chart</SelectItem>
            <SelectItem value="pie">Pie Chart</SelectItem>
            <SelectItem value="area">Area Chart</SelectItem>
            <SelectItem value="doughnut">Doughnut Chart</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Real-Time Chart Preview (R3.3) */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Chart Preview
        </Label>
        <div className="border rounded-lg overflow-hidden bg-gray-50">
          {!config.config?.source ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
              <Info className="h-4 w-4 mr-2" />
              Select a data source to see preview
            </div>
          ) : !planId ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
              <Info className="h-4 w-4 mr-2" />
              Select a business plan to see preview
            </div>
          ) : previewLoading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="flex items-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-blue-600" />
                <span className="text-sm">Loading preview...</span>
              </div>
            </div>
          ) : previewError ? (
            <div className="h-48 flex items-center justify-center text-amber-600 text-sm">
              <Info className="h-4 w-4 mr-2" />
              {previewError}
            </div>
          ) : previewData?.labels?.length > 0 ? (
            <ChartPreviewInline 
              data={previewData} 
              chartType={config.config?.chart_type || 'bar'}
            />
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
              <Info className="h-4 w-4 mr-2" />
              No data available for preview
            </div>
          )}
        </div>
      </div>

      {/* Time Period Configuration */}
      <TimePeriodConfig config={config} setConfig={setConfig} dataSources={dataSources} />

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={config.config?.show_legend !== false}
            onCheckedChange={(checked) => setConfig({
              ...config,
              config: { ...config.config, show_legend: checked }
            })}
          />
          <Label>Show Legend</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={config.config?.show_data_labels}
            onCheckedChange={(checked) => setConfig({
              ...config,
              config: { ...config.config, show_data_labels: checked }
            })}
          />
          <Label>Show Data Labels</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={config.config?.show_comparison}
            onCheckedChange={(checked) => setConfig({
              ...config,
              config: { ...config.config, show_comparison: checked }
            })}
          />
          <Label>Show Actual vs Planned</Label>
        </div>
      </div>
    </div>
  );
};

// Inline Chart Preview Component (uses Chart.js directly)
const ChartPreviewInline = ({ data, chartType }) => {
  const canvasRef = React.useRef(null);
  const chartRef = React.useRef(null);
  
  React.useEffect(() => {
    if (!canvasRef.current || !data?.labels?.length) return;
    
    // Destroy existing chart
    if (chartRef.current) {
      chartRef.current.destroy();
    }
    
    // Dynamic import Chart.js to avoid SSR issues
    import('chart.js/auto').then(({ default: Chart }) => {
      const ctx = canvasRef.current.getContext('2d');
      
      const colors = [
        'rgba(59, 130, 246, 0.7)',
        'rgba(34, 197, 94, 0.7)',
        'rgba(234, 179, 8, 0.7)',
        'rgba(168, 85, 247, 0.7)',
        'rgba(236, 72, 153, 0.7)',
      ];
      
      const chartData = {
        labels: data.labels,
        datasets: data.datasets?.map((ds, i) => ({
          ...ds,
          backgroundColor: ds.backgroundColor || colors[i % colors.length],
          borderColor: ds.borderColor || colors[i % colors.length].replace('0.7', '1'),
          borderWidth: 1,
          fill: chartType === 'area',
        })) || []
      };
      
      chartRef.current = new Chart(ctx, {
        type: chartType === 'area' ? 'line' : chartType,
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: data.datasets?.length > 1, position: 'top' },
          },
          scales: chartType !== 'pie' && chartType !== 'doughnut' ? {
            y: { beginAtZero: true }
          } : undefined
        }
      });
    });
    
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [data, chartType]);
  
  return (
    <div className="h-48 p-2">
      <canvas ref={canvasRef} />
    </div>
  );
};

// ==========================================
// Custom Table Configurator
// ==========================================
const CustomTableConfigurator = ({ config, setConfig }) => {
  const [columns, setColumns] = useState(config.config?.columns || []);
  const [rows, setRows] = useState(config.config?.rows || []);

  const addColumn = () => {
    const newCol = { key: `col_${columns.length}`, label: `Column ${columns.length + 1}` };
    const updatedColumns = [...columns, newCol];
    setColumns(updatedColumns);
    const updatedRows = rows.map(row => ({ ...row, [newCol.key]: '' }));
    setRows(updatedRows);
    setConfig({
      ...config,
      config: { ...config.config, columns: updatedColumns, rows: updatedRows }
    });
  };

  const updateColumn = (index, label) => {
    const updated = columns.map((col, i) => i === index ? { ...col, label } : col);
    setColumns(updated);
    setConfig({ ...config, config: { ...config.config, columns: updated } });
  };

  const removeColumn = (index) => {
    const colKey = columns[index].key;
    const updated = columns.filter((_, i) => i !== index);
    const updatedRows = rows.map(row => {
      const newRow = { ...row };
      delete newRow[colKey];
      return newRow;
    });
    setColumns(updated);
    setRows(updatedRows);
    setConfig({ ...config, config: { ...config.config, columns: updated, rows: updatedRows } });
  };

  const addRow = () => {
    const newRow = {};
    columns.forEach(col => { newRow[col.key] = ''; });
    const updatedRows = [...rows, newRow];
    setRows(updatedRows);
    setConfig({ ...config, config: { ...config.config, rows: updatedRows } });
  };

  const updateCell = (rowIndex, colKey, value) => {
    const updatedRows = rows.map((row, i) => i === rowIndex ? { ...row, [colKey]: value } : row);
    setRows(updatedRows);
    setConfig({ ...config, config: { ...config.config, rows: updatedRows } });
  };

  const removeRow = (rowIndex) => {
    const updatedRows = rows.filter((_, i) => i !== rowIndex);
    setRows(updatedRows);
    setConfig({ ...config, config: { ...config.config, rows: updatedRows } });
  };

  return (
    <div className="space-y-4">
      {/* AI Summary Toggle */}
      <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <Sparkles className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-amber-800">AI-Generated Summary</span>
            <Checkbox
              checked={config.config?.include_ai_summary !== false}
              onCheckedChange={(checked) => setConfig({
                ...config,
                config: { ...config.config, include_ai_summary: checked }
              })}
            />
          </div>
          <p className="text-xs text-amber-700 mt-1">
            AI will automatically summarize this table when you generate the report
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Columns</Label>
          <Button variant="outline" size="sm" onClick={addColumn}>Add Column</Button>
        </div>
        <div className="space-y-2">
          {columns.map((col, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={col.label}
                onChange={(e) => updateColumn(index, e.target.value)}
                placeholder="Column name"
                className="flex-1"
              />
              <Button variant="ghost" size="icon" onClick={() => removeColumn(index)} className="text-red-500">
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {columns.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Table Data ({rows.length} rows)</Label>
            <Button variant="outline" size="sm" onClick={addRow}>Add Row</Button>
          </div>
          
          {rows.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {columns.map((col, i) => (
                        <th key={i} className="px-3 py-2 text-left font-medium text-gray-700">{col.label}</th>
                      ))}
                      <th className="px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50">
                        {columns.map((col, colIndex) => (
                          <td key={colIndex} className="px-1 py-1">
                            <Input
                              value={row[col.key] || ''}
                              onChange={(e) => updateCell(rowIndex, col.key, e.target.value)}
                              className="h-8 text-sm border-0 focus:ring-1"
                              placeholder="Enter value"
                            />
                          </td>
                        ))}
                        <td className="px-1 py-1">
                          <Button variant="ghost" size="icon" onClick={() => removeRow(rowIndex)} className="h-8 w-8 text-red-500">
                            <X className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {rows.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4 border rounded-lg">
              No rows yet. Click "Add Row" to start adding data.
            </p>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Checkbox
          checked={config.config?.show_totals}
          onCheckedChange={(checked) => setConfig({
            ...config,
            config: { ...config.config, show_totals: checked }
          })}
        />
        <Label>Show totals row (for numeric columns)</Label>
      </div>
    </div>
  );
};

// ==========================================
// Cover Page Configurator
// ==========================================
const CoverPageConfigurator = ({ config, setConfig }) => {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Report Title</Label>
        <Input
          value={config.config?.title || 'Business Plan'}
          onChange={(e) => setConfig({
            ...config,
            config: { ...config.config, title: e.target.value }
          })}
        />
      </div>

      <div className="space-y-2">
        <Label>Subtitle</Label>
        <Input
          value={config.config?.subtitle || ''}
          onChange={(e) => setConfig({
            ...config,
            config: { ...config.config, subtitle: e.target.value }
          })}
          placeholder="Use {{company_name}} for dynamic value"
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={config.config?.show_logo !== false}
            onCheckedChange={(checked) => setConfig({
              ...config,
              config: { ...config.config, show_logo: checked }
            })}
          />
          <Label>Show Company Logo</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={config.config?.show_date !== false}
            onCheckedChange={(checked) => setConfig({
              ...config,
              config: { ...config.config, show_date: checked }
            })}
          />
          <Label>Show Date</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={config.config?.show_prepared_for}
            onCheckedChange={(checked) => setConfig({
              ...config,
              config: { ...config.config, show_prepared_for: checked }
            })}
          />
          <Label>Show "Prepared For" Section</Label>
        </div>
      </div>

      {config.config?.show_prepared_for && (
        <div className="space-y-2">
          <Label>Prepared For</Label>
          <Input
            value={config.config?.prepared_for_value || ''}
            onChange={(e) => setConfig({
              ...config,
              config: { ...config.config, prepared_for_value: e.target.value }
            })}
            placeholder="e.g., Commercial Bank of Ethiopia"
          />
        </div>
      )}
    </div>
  );
};

// ==========================================
// Table of Contents Configurator
// ==========================================
const TOCConfigurator = ({ config, setConfig }) => {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Depth</Label>
        <Select
          value={String(config.config?.depth || 2)}
          onValueChange={(value) => setConfig({
            ...config,
            config: { ...config.config, depth: parseInt(value) }
          })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Level 1 only (Main sections)</SelectItem>
            <SelectItem value="2">Level 2 (Sections + Subsections)</SelectItem>
            <SelectItem value="3">Level 3 (All headings)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-sm text-gray-500">
        Table of contents will be automatically generated based on your report sections.
      </p>
    </div>
  );
};

// ==========================================
// Section Configurator (Multi-Tab Selection)
// ==========================================
const SectionConfigurator = ({ config, setConfig, sectionType, sectionTitle, tabs }) => {
  // Get currently selected tabs (default: all selected)
  const selectedTabs = config.config?.selected_tabs || tabs.map(t => t.id);
  
  const toggleTab = (tabId) => {
    const current = config.config?.selected_tabs || tabs.map(t => t.id);
    const updated = current.includes(tabId)
      ? current.filter(id => id !== tabId)
      : [...current, tabId];
    
    setConfig({
      ...config,
      config: { 
        ...config.config, 
        section_type: sectionType,
        selected_tabs: updated 
      }
    });
  };
  
  const selectAll = () => {
    setConfig({
      ...config,
      config: { 
        ...config.config, 
        section_type: sectionType,
        selected_tabs: tabs.map(t => t.id) 
      }
    });
  };
  
  const selectNone = () => {
    setConfig({
      ...config,
      config: { 
        ...config.config, 
        section_type: sectionType,
        selected_tabs: [] 
      }
    });
  };

  return (
    <div className="space-y-4">
      {/* AI Description Toggle */}
      <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <Sparkles className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-amber-800">AI-Generated Summary</span>
            <Checkbox
              checked={config.config?.include_ai_summary !== false}
              onCheckedChange={(checked) => setConfig({
                ...config,
                config: { ...config.config, include_ai_summary: checked }
              })}
            />
          </div>
          <p className="text-xs text-amber-700 mt-1">
            AI will automatically generate a summary and insights for this section
          </p>
        </div>
      </div>

      {/* Tab Selection */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Include Tabs</Label>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs h-7">
              Select All
            </Button>
            <Button variant="ghost" size="sm" onClick={selectNone} className="text-xs h-7">
              Clear
            </Button>
          </div>
        </div>
        
        <div className="space-y-2 max-h-60 overflow-y-auto p-2 bg-gray-50 rounded-lg border">
          {tabs.map((tab) => (
            <div 
              key={tab.id}
              className={`
                flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer
                ${selectedTabs.includes(tab.id) 
                  ? 'bg-blue-50 border-blue-200' 
                  : 'bg-white border-gray-200 hover:border-gray-300'}
              `}
              onClick={() => toggleTab(tab.id)}
            >
              <Checkbox
                checked={selectedTabs.includes(tab.id)}
                onCheckedChange={() => toggleTab(tab.id)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{tab.label}</p>
                <p className="text-xs text-gray-500">{tab.description}</p>
              </div>
            </div>
          ))}
        </div>
        
        <p className="text-xs text-gray-500">
          Selected: {selectedTabs.length} of {tabs.length} tabs
        </p>
      </div>

      {/* Display Options */}
      <div className="space-y-3 p-3 bg-gray-50 rounded-lg border">
        <Label className="text-sm font-medium">Display Options</Label>
        
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={config.config?.include_charts !== false}
              onCheckedChange={(checked) => setConfig({
                ...config,
                config: { ...config.config, include_charts: checked }
              })}
            />
            <Label className="text-sm font-normal">Include Charts & Visualizations</Label>
          </div>
          
          <div className="flex items-center gap-2">
            <Checkbox
              checked={config.config?.include_tables !== false}
              onCheckedChange={(checked) => setConfig({
                ...config,
                config: { ...config.config, include_tables: checked }
              })}
            />
            <Label className="text-sm font-normal">Include Data Tables</Label>
          </div>
          
          <div className="flex items-center gap-2">
            <Checkbox
              checked={config.config?.include_kpis}
              onCheckedChange={(checked) => setConfig({
                ...config,
                config: { ...config.config, include_kpis: checked }
              })}
            />
            <Label className="text-sm font-normal">Include KPI Summaries</Label>
          </div>
        </div>
      </div>

      {/* Detail Level */}
      <div className="space-y-2">
        <Label>Detail Level</Label>
        <Select
          value={config.config?.detail_level || 'standard'}
          onValueChange={(value) => setConfig({
            ...config,
            config: { ...config.config, detail_level: value }
          })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="summary">Summary - Key highlights only</SelectItem>
            <SelectItem value="standard">Standard - Balanced detail</SelectItem>
            <SelectItem value="detailed">Detailed - Full data tables</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <p className="text-xs text-gray-500 bg-blue-50 p-2 rounded border border-blue-200">
        💡 Time period for the report data is configured when you generate the report, allowing you to reuse this template for different periods.
      </p>
    </div>
  );
};

export default BlockConfigurator;
